import { types as api } from '@mesa-labs/mesa-api';
import React, { useState } from 'react';
import styled from 'styled-components';

import Linkified from './Linkified';
import SourceNoteQuote from './SourceNoteQuote';
import { LinkButton } from './styles';
import { formatDateTime } from '../../../../utils';
import {
  formatAssigneeEmail, timelineEntryChange, timelineEntryText, TimelineEntryChange,
} from '../../../../utils/vendorActionItems';

const List = styled.ol`
  border-left: 2px solid #EAECF0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0 0 0 14px;
`;

const Entry = styled.li<{ $note: boolean }>`
  color: ${(p) => (p.$note ? '#101828' : p.theme.colors.Gray500)};
  font-size: ${(p) => (p.$note ? '14px' : '11px')};
  position: relative;
  white-space: pre-wrap;

  &::before {
    background: ${(p) => (p.$note ? '#7F56D9' : p.theme.colors.Gray300)};
    border-radius: 999px;
    content: '';
    height: 8px;
    left: -19px;
    position: absolute;
    top: ${(p) => (p.$note ? '3px' : '4px')};
    width: 8px;
  }
`;

const EntryMeta = styled.div`
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 11px;
  margin-bottom: 2px;
`;

const Change = styled.dl`
  display: grid;
  gap: 4px 10px;
  grid-template-columns: auto 1fr;
  margin: 6px 0 0;
`;

const ChangeLabel = styled.dt`
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 11px;
`;

const ChangeValue = styled.dd<{ $empty: boolean }>`
  background: #F9FAFB;
  border-radius: 4px;
  color: ${(p) => (p.$empty ? p.theme.colors.Gray500 : '#101828')};
  font-size: 12px;
  font-style: ${(p) => (p.$empty ? 'italic' : 'normal')};
  margin: 0;
  overflow-wrap: anywhere;
  padding: 4px 8px;
`;

function ChangeDetail({ change }: { change: TimelineEntryChange }): React.ReactElement {
  return (
    <Change>
      <ChangeLabel>Before</ChangeLabel>
      <ChangeValue $empty={!change.before}>{change.before || '(empty)'}</ChangeValue>
      <ChangeLabel>After</ChangeLabel>
      <ChangeValue $empty={!change.after}>{change.after || '(empty)'}</ChangeValue>
    </Change>
  );
}

// Renders entries in the order given; the drawer passes them newest first.
export default function ActionItemTimeline({ entries }: { entries: readonly api.VendorActionItemTimelineEntry[] }): React.ReactElement {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) => setExpandedIds((ids) => {
    const next = new Set(ids);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  return (
    <List>
      {entries.map((entry) => {
        const { note, sourceNote } = entry;
        if (entry.kind === api.VendorActionItemTimelineEntryKind.NOTE && note) {
          return (
            <Entry key={entry.id} $note>
              <EntryMeta>{`${formatAssigneeEmail(note.createdBy)} · ${formatDateTime(note.createdAt)}`}</EntryMeta>
              <Linkified text={note.note} />
            </Entry>
          );
        }
        const change = timelineEntryChange(entry);
        const expanded = expandedIds.has(entry.id);
        return (
          <Entry key={entry.id} $note={false}>
            {`${timelineEntryText(entry)} · ${formatAssigneeEmail(entry.actor)} · ${formatDateTime(entry.createdAt)}`}
            {change && (
              <>
                {' · '}
                <LinkButton type="button" aria-expanded={expanded} onClick={() => toggle(entry.id)} style={{ fontSize: 11 }}>
                  {expanded ? 'Hide changes' : 'Show changes'}
                </LinkButton>
                {expanded && <ChangeDetail change={change} />}
              </>
            )}
            {sourceNote && <div style={{ marginTop: 6 }}><SourceNoteQuote note={sourceNote} /></div>}
          </Entry>
        );
      })}
    </List>
  );
}
